import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import type { BookDto } from '../api/books.js';
import { ApiError, userErrorMessage } from '../api/errors.js';
import {
  illustrationsApi,
  type IllustrationStatus,
  type IllustrationVariantDto,
} from '../api/illustrations.js';
import { monetizationApi, redirectToCheckout } from '../api/monetization.js';
import { useLegal } from '../brand/legal-context.js';
import { useI18n } from '../i18n/context.js';
import { coverAspect } from './artwork-aspect.js';
import { ReplacementPackModal } from './ReplacementPackModal.js';
import { GenerationFailed } from '../components/GenerationFailed.js';

type CoverChangeRequest =
  | { readonly side: 'front' | 'back'; readonly mode: 'generate' }
  | {
      readonly side: 'front' | 'back';
      readonly mode: 'edit';
      readonly variantId: string;
      readonly guidance: string;
    };

export function CoverStep({ book }: { book: BookDto }): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [pendingSide, setPendingSide] = useState<'front' | 'back' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replacementRequest, setReplacementRequest] = useState<CoverChangeRequest | null>(null);
  const query = useQuery({
    queryKey: ['book-cover', book.id],
    queryFn: () => illustrationsApi.cover(book.id),
    refetchInterval: (current) => {
      const cover = current.state.data;
      const active =
        cover && (isGenerationActive(cover.status) || isGenerationActive(cover.backStatus));
      return active ? 3_000 : false;
    },
  });

  /*
   * The server finishes a step's work on the step's own endpoint (it syncs finished
   * generations as it answers), so the book the sidebar reads learns of it only when
   * refetched. Without this a finished step stayed "Empty" or "In progress" until reload.
   * Only when what the step holds changes, not on every poll while it generates.
   */
  const stepState = query.data ? `${query.data.status}:${query.data.backStatus}` : '';
  useEffect(() => {
    if (stepState) void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  }, [book.id, stepState, queryClient]);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['book-cover', book.id] }),
      queryClient.invalidateQueries({ queryKey: ['book', book.id] }),
    ]);
  const runSelection = (side: 'front' | 'back', action: Promise<unknown>): void => {
    setPendingSide(side);
    setError(null);
    void action
      .then(refresh)
      .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
      .finally(() => setPendingSide(null));
  };
  const submitChange = (request: CoverChangeRequest): void => {
    const action =
      request.mode === 'edit'
        ? request.side === 'front'
          ? illustrationsApi.editCover(book.id, request.variantId, request.guidance)
          : illustrationsApi.editBackCover(book.id, request.variantId, request.guidance)
        : request.side === 'front'
          ? illustrationsApi.generateCover(book.id)
          : illustrationsApi.generateBackCover(book.id);
    setPendingSide(request.side);
    setError(null);
    void action
      .then(refresh)
      .catch((reason: unknown) => {
        if (reason instanceof ApiError && reason.code === 'plan_limit_exceeded') {
          setReplacementRequest(request);
          return;
        }
        setError(userErrorMessage(reason, t));
      })
      .finally(() => setPendingSide(null));
  };
  const legal = useLegal();
  const replacementPack = useMutation({
    mutationFn: () =>
      monetizationApi.buyImageReplacementPack(book.id, legal.documentVersionId, 'cover'),
    onSuccess: (result) => redirectToCheckout(result),
  });
  if (query.isPending) return <p>{t.common.loading}</p>;
  if (query.isError) return <p role="alert">{query.error.message}</p>;
  const cover = query.data;
  const generationActive = isGenerationActive(cover.status) || isGenerationActive(cover.backStatus);
  const generationBlocked = pendingSide !== null || generationActive;

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t.cover.title}</h1>
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.cover.subtitle}</p>
        {/* The words the picture is drawn with: before, a parent had to remember them from the story step. */}
        {book.coverTitle ? (
          <p className="mt-2 text-sm">{t.cover.titleLine(book.coverTitle)}</p>
        ) : null}
      </div>
      {/* A pair, side by side even on a phone: stacked, the two covers were two screens. */}
      <div className="grid grid-cols-2 gap-4 sm:gap-8">
        <CoverSidePanel
          title={t.cover.frontTitle}
          empty={t.cover.frontEmpty}
          previewAlt={t.cover.previewAlt}
          variantAlt={t.cover.variantAlt}
          assetId={cover.coverAssetId}
          status={cover.status}
          lastError={cover.lastError}
          variants={cover.variants}
          aspect={coverAspect(book.attributes)}
          pending={pendingSide === 'front'}
          generationBlocked={generationBlocked}
          onGenerate={() => submitChange({ side: 'front', mode: 'generate' })}
          onSelect={(variantId) =>
            runSelection('front', illustrationsApi.selectCoverVariant(book.id, variantId))
          }
          onEdit={(variantId, guidance) =>
            submitChange({ side: 'front', mode: 'edit', variantId, guidance })
          }
        />
        <CoverSidePanel
          title={t.cover.backTitle}
          empty={t.cover.backEmpty}
          previewAlt={t.cover.backPreviewAlt}
          variantAlt={t.cover.backVariantAlt}
          assetId={cover.backCoverAssetId}
          status={cover.backStatus}
          lastError={cover.backLastError}
          variants={cover.backVariants}
          aspect={coverAspect(book.attributes)}
          pending={pendingSide === 'back'}
          generationBlocked={generationBlocked}
          onGenerate={() => submitChange({ side: 'back', mode: 'generate' })}
          onSelect={(variantId) =>
            runSelection('back', illustrationsApi.selectBackCoverVariant(book.id, variantId))
          }
          onEdit={(variantId, guidance) =>
            submitChange({ side: 'back', mode: 'edit', variantId, guidance })
          }
        />
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {replacementRequest ? (
        <ReplacementPackModal
          pending={replacementPack.isPending}
          error={replacementPack.isError}
          onClose={() => setReplacementRequest(null)}
          onPay={() => replacementPack.mutate()}
        />
      ) : null}
    </div>
  );
}

function CoverSidePanel({
  title,
  empty,
  previewAlt,
  variantAlt,
  assetId,
  status,
  lastError,
  variants,
  aspect,
  pending,
  generationBlocked,
  onGenerate,
  onSelect,
  onEdit,
}: {
  readonly title: string;
  readonly empty: string;
  readonly previewAlt: string;
  readonly variantAlt: string;
  readonly assetId: string | null;
  readonly status: IllustrationStatus;
  readonly lastError: string | null;
  readonly variants: readonly IllustrationVariantDto[];
  readonly aspect: CSSProperties;
  readonly pending: boolean;
  readonly generationBlocked: boolean;
  onGenerate(): void;
  onSelect(variantId: string): void;
  onEdit(variantId: string, guidance: string): void;
}): JSX.Element {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [guidance, setGuidance] = useState('');
  const selected = variants.find((variant) => variant.assetId === assetId) ?? variants.at(-1);

  return (
    <section className="grid content-start gap-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {selected ? (
        <img
          src={selected.url}
          alt={previewAlt}
          className="w-full max-w-[360px] rounded-xl bg-slate-100 object-cover"
          style={aspect}
        />
      ) : (
        <div
          className="grid w-full max-w-[360px] place-items-center rounded-xl bg-slate-100 text-sm text-[var(--kitup-color-muted)]"
          style={aspect}
        >
          {empty}
        </div>
      )}
      <span className={`kitup-status kitup-status-${status} w-fit`}>
        {t.illustrations.statuses[status]}
      </span>
      {lastError ? <GenerationFailed error={lastError} /> : null}
      {/*
        A finished cover's loudest button used to be the one that throws it away. Once there
        is a cover, drawing another is a secondary action beside Edit, and it asks first.
      */}
      <div className="flex flex-wrap gap-2">
        {selected ? (
          <button
            type="button"
            className="kitup-secondary-button"
            disabled={generationBlocked}
            aria-expanded={editing}
            onClick={() => setEditing((current) => !current)}
          >
            {t.common.editImage}
          </button>
        ) : null}
        <button
          type="button"
          className={variants.length > 0 ? 'kitup-secondary-button' : 'kitup-primary-button'}
          disabled={generationBlocked}
          onClick={() => {
            if (variants.length > 0 && !window.confirm(t.cover.regenerateConfirm)) return;
            onGenerate();
          }}
        >
          {pending
            ? t.cover.generating
            : variants.length > 0
              ? t.cover.regenerate
              : t.cover.generate}
        </button>
      </div>
      {selected && editing ? (
        <form
          className="grid gap-2 rounded-xl border border-[var(--kitup-color-border)] p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const value = guidance.trim();
            if (!value) return;
            onEdit(selected.id, value);
            setGuidance('');
            setEditing(false);
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="font-medium">{t.common.imageEditGuidance}</span>
            <textarea
              className="kitup-input min-h-24 resize-y"
              value={guidance}
              maxLength={1_000}
              disabled={generationBlocked}
              placeholder={t.common.imageEditPlaceholder}
              onChange={(event) => setGuidance(event.target.value)}
            />
          </label>
          <p className="text-xs text-[var(--kitup-color-muted)]">{t.common.imageEditHint}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className="kitup-primary-button"
              disabled={generationBlocked || !guidance.trim()}
            >
              {pending ? t.common.imageEditing : t.common.applyImageEdit}
            </button>
            <button
              type="button"
              className="kitup-quiet-button"
              disabled={generationBlocked}
              onClick={() => {
                setGuidance('');
                setEditing(false);
              }}
            >
              {t.common.cancel}
            </button>
          </div>
        </form>
      ) : null}
      {variants.length > 1 ? (
        <div>
          <p className="mb-2 text-sm font-medium">{t.cover.variants}</p>
          <div className="flex gap-2 overflow-x-auto">
            {variants.map((variant) => (
              <button
                key={variant.id}
                type="button"
                aria-pressed={assetId === variant.assetId}
                className="shrink-0 rounded-lg border-2 p-1 aria-pressed:border-[var(--kitup-color-accent)]"
                onClick={() => onSelect(variant.id)}
              >
                <img
                  src={variant.url}
                  alt={variantAlt}
                  className="h-28 w-20 rounded object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function isGenerationActive(status: IllustrationStatus): boolean {
  return status === 'queued' || status === 'generating';
}
